
import {
  makeApiRequest,
  tsToDate,
  fetchPSXSymbols,
} from "./helpers.js";

import {
  subscribeOnStream,
  unsubscribeFromStream,
} from "./streaming.js";

// ─── Cache ────────────────────────────────────────────────────────────────────

const lastBarsCache = new Map();

let symbolsCache = null;

async function getPSXSymbols() {
  if (!symbolsCache) {
    symbolsCache = fetchPSXSymbols().catch((error) => {
      symbolsCache = null;
      throw error;
    });
  }

  return symbolsCache;
}

// Normalize "PSX:OGDC" and "OGDC" to "OGDC".
function normalizeSymbol(symbol) {
  return String(symbol || "")
    .trim()
    .split(":")
    .pop()
    .trim()
    .toUpperCase();
}

// ─── Configuration ───────────────────────────────────────────────────────────

const configurationData = {
  supported_resolutions: ["1D", "1W", "1M"],

  exchanges: [
    {
      value: "PSX",
      name: "PSX",
      desc: "Pakistan Stock Exchange",
    },
  ],

  symbols_types: [
    {
      name: "Stock",
      value: "stock",
    },
  ],
};

// ─── Datafeed ─────────────────────────────────────────────────────────────────

const datafeed = {
  // ─── Initialize datafeed ─────────────────────────────────────────────────

  onReady: (callback) => {
    console.log("[onReady]");

    setTimeout(() => {
      callback(configurationData);
    }, 0);
  },

  // ─── Built-in TradingView symbol search ──────────────────────────────────

  searchSymbols: async (
    userInput,
    exchange,
    _symbolType,
    onResultReadyCallback
  ) => {
    console.log("[searchSymbols]", userInput, exchange);

    try {
      const list = await getPSXSymbols();
      const query = String(userInput || "").trim().toLowerCase();

      const filtered = list
        .filter((item) => {
          const code = String(item.code || "").toLowerCase();
          const name = String(item.name || "").toLowerCase();

          const exchangeMatches =
            !exchange ||
            exchange.toUpperCase() === "PSX";

          const queryMatches =
            !query ||
            code.includes(query) ||
            name.includes(query);

          return exchangeMatches && queryMatches;
        })
        .map((item) => ({
          symbol: item.code,
          ticker: `PSX:${item.code}`,
          full_name: `PSX:${item.code}`,
          description: item.name || item.code,
          exchange: "PSX",
          type: "stock",
        }));

      console.log(
        `[searchSymbols] Found ${filtered.length} symbols`
      );

      onResultReadyCallback(filtered);
    } catch (error) {
      console.error("[searchSymbols] error:", error);
      onResultReadyCallback([]);
    }
  },

  // ─── Resolve selected symbol ─────────────────────────────────────────────

  resolveSymbol: async (
    symbolName,
    onSymbolResolvedCallback,
    onResolveErrorCallback
  ) => {
    console.log("[resolveSymbol]", symbolName);

    try {
      const code = normalizeSymbol(symbolName);
      const list = await getPSXSymbols();

      const sym = list.find(
        (item) =>
          String(item.code || "").trim().toUpperCase() === code
      );

      if (!sym) {
        console.warn(
          "[resolveSymbol] Symbol not found:",
          symbolName
        );

        onResolveErrorCallback(
          `Symbol not found: ${symbolName}`
        );
        return;
      }

      const symbolInfo = {
        ticker: `PSX:${sym.code}`,
        name: sym.code,
        description: sym.name || sym.code,

        type: "stock",

        // Daily historical data only.
        session: "0915-1530",
        timezone: "Asia/Karachi",

        exchange: "PSX",
        listed_exchange: "PSX",

        minmov: 1,
        pricescale: 100,

        has_intraday: false,
        has_daily: true,
        has_weekly_and_monthly: false,

        supported_resolutions:
          configurationData.supported_resolutions,

        volume_precision: 0,
        data_status: "endofday",
      };

      console.log("[resolveSymbol] Resolved:", symbolInfo);

      onSymbolResolvedCallback(symbolInfo);
    } catch (error) {
      console.error("[resolveSymbol] error:", error);

      onResolveErrorCallback(
        error?.message || "Unable to resolve PSX symbol"
      );
    }
  },

  // ─── Fetch historical OHLCV bars ─────────────────────────────────────────

  getBars: async (
    symbolInfo,
    resolution,
    periodParams,
    onHistoryCallback,
    onErrorCallback
  ) => {
    const {
      from,
      to,
      firstDataRequest,
    } = periodParams;

    const code = normalizeSymbol(
      symbolInfo.ticker || symbolInfo.name
    );

    console.log(
      "[getBars]",
      code,
      resolution,
      tsToDate(from),
      "→",
      tsToDate(to)
    );

    try {
      const fromDate = tsToDate(from);
      const toDate = tsToDate(to);

      const data = await makeApiRequest(
        `api/chart/${encodeURIComponent(code)}?from_date=${encodeURIComponent(
          fromDate
        )}&to_date=${encodeURIComponent(toDate)}&is_index=`
      );

      const candles = Array.isArray(data?.candles)
        ? data.candles
        : [];

      const bars = candles
        .map((candle) => {
          const timestamp = new Date(candle.timestamp).getTime();

          return {
            time: timestamp,
            open: Number(candle.open_price),
            high: Number(candle.high_price),
            low: Number(candle.low_price),
            close: Number(candle.close_price),
            volume: Number.parseInt(candle.volume, 10) || 0,
          };
        })
        .filter(
          (bar) =>
            Number.isFinite(bar.time) &&
            Number.isFinite(bar.open) &&
            Number.isFinite(bar.high) &&
            Number.isFinite(bar.low) &&
            Number.isFinite(bar.close)
        )
        .sort((a, b) => a.time - b.time);

      // TradingView expects bar times in ascending order.
      // Avoid duplicate timestamps.
      const uniqueBars = bars.filter(
        (bar, index, array) =>
          index === 0 ||
          bar.time !== array[index - 1].time
      );

      if (firstDataRequest && uniqueBars.length > 0) {
        lastBarsCache.set(code, {
          ...uniqueBars[uniqueBars.length - 1],
        });
      }

      console.log(
        `[getBars] Returning ${uniqueBars.length} bars for ${code}`
      );

      onHistoryCallback(uniqueBars, {
        noData: uniqueBars.length === 0,
      });
    } catch (error) {
      console.error("[getBars] error:", error);

      onErrorCallback(
        error?.message || "Failed to fetch historical chart data"
      );
    }
  },

  // ─── Realtime subscription ───────────────────────────────────────────────

  subscribeBars: (
    symbolInfo,
    resolution,
    onRealtimeCallback,
    subscriberUID,
    onResetCacheNeededCallback
  ) => {
    const code = normalizeSymbol(
      symbolInfo.ticker || symbolInfo.name
    );

    console.log(
      "[subscribeBars]",
      code,
      subscriberUID
    );

    subscribeOnStream(
      {
        ...symbolInfo,
        ticker: code,
        name: code,
      },
      resolution,
      onRealtimeCallback,
      subscriberUID,
      onResetCacheNeededCallback,
      lastBarsCache.get(code)
    );
  },

  // ─── Unsubscribe from realtime updates ───────────────────────────────────

  unsubscribeBars: (subscriberUID) => {
    console.log("[unsubscribeBars]", subscriberUID);

    unsubscribeFromStream(subscriberUID);
  },
};

export default datafeed;
